import { autoinject, bindable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { connectTo } from 'aurelia-store';
import { ValidationController, Validator } from 'aurelia-validation';
import { SearchResult } from 'elements/search/search-input';
import { ApplicationState } from 'lib/state';
import { AsyncErrorHandler } from 'lib/ui';
import { StandardValidationBuilder } from 'lib/validation';
import { AddHoursProductionOrderDto, MooringProductionTaskType, MooringProductionTaskTypeOptions } from 'models/mooring';
import { UserService } from 'services/user-service';

@autoinject
@connectTo()
export class CreateHourForm {
  protected loading = true;

  @bindable
  onSave: (data: AddHoursProductionOrderDto) => Promise<void>;

  @bindable
  onCancel: () => void;

  @bindable
  private model = new AddHoursProductionOrderDto();

  protected users: SearchResult[] = [];

  @bindable
  protected options: MooringProductionTaskType[] = [];

  protected taskOptions: { title: string; id: MooringProductionTaskType; meta: string }[] = [];

  protected state: ApplicationState;

  constructor(
    private i18n: I18N,
    private validationController: ValidationController,
    private vb: StandardValidationBuilder,
    protected validator: Validator,
    private userService: UserService
  ) {}

  protected addValidationRules() {
    this.vb.with(this.model).required('Date').required('Hours').min('Hours', 0.1).required('UserId').required('Task').min('Task', 0).done();
  }

  protected bind() {
    if (!this.onSave) {
      throw new Error('onSave is required');
    }

    this.addValidationRules();
    void this.init();
  }

  protected attached() {
    console.log('CreateHourForm attached', this.options);
    if (!this.options?.length) {
      this.taskOptions = MooringProductionTaskTypeOptions.map((task) => ({
        id: task.id,
        title: this.i18n.tr(task.label),
        meta: '',
      }));
    } else {
      const taskMap = new Map(MooringProductionTaskTypeOptions.map((task) => [task.id, task]));
      this.taskOptions = this.options.map((task) => ({
        id: task,
        meta: '',
        title: this.i18n.tr(taskMap.get(task)?.label || '?'),
      }));
    }
  }

  protected async init() {
    await this.loadUsers();

    if (this.users.find((user) => user.id === this.state.user.Id)) {
      this.model.UserId = this.state.user.Id;
    }

    this.loading = false;
  }

  protected async loadUsers() {
    const { data } = await this.userService.getMooringUsers();
    this.users = data.map((user) => ({
      id: user.Id,
      title: user.Name,
      meta: '',
    }));
  }

  @AsyncErrorHandler
  protected async onSubmit() {
    const result = await this.validationController.validate();
    if (result.valid) {
      await this.onSave(this.model);
    }
  }

  protected getUserInfo(id: string) {
    const user = this.users.find((user) => user.id === id);
    if (!user) {
      return null;
    }
    return user;
  }

  protected findUsers(term: string): SearchResult[] {
    if (!term) {
      return this.users;
    }

    return this.users.filter((option) => option.title.toLowerCase().includes(term.toLowerCase()));
  }

  protected userSelected(item: { id: string }) {
    this.model.UserId = item.id;
  }

  protected getTaskInfo(id: number) {
    const option = this.taskOptions.find((task) => task.id === id);
    if (!option) {
      return null;
    }

    return {
      id: option.id,
      title: option.title,
    };
  }

  protected findTasks(term: string): SearchResult[] {
    if (!term) {
      return this.taskOptions;
    }

    return this.taskOptions.filter((option) => option.title.toLowerCase().includes(term.toLowerCase()));
  }

  protected taskSelected(item: { id: number }) {
    this.model.Task = item.id as MooringProductionTaskType;
  }

  protected addQuarter() {
    this.model.Hours = (+this.model.Hours || 0) + 0.25;
  }

  protected removeQuarter() {
    this.model.Hours = Math.max(0, +this.model.Hours - 0.25);
  }
}
